import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DetailPanel, DetailPlaceholder } from "@/components/DataList";
import { TableToolbar } from "@/components/TableToolbar";
import { useTableView, type TableColumn } from "@/components/TableView";
import { Badge, Card } from "@/components/ui";
import { CompanyPanel } from "@/features/companies/CompanyPanel";
import { companyListQuery, type Company } from "@/features/companies/api";
import { cn } from "@/lib/cn";
import type { ExportColumn } from "@/lib/exportXlsx";

// Settings → Companies: the companies of the organization and the letterhead
// printed on their documents (ADR-031). A tenant has a handful of companies,
// so this is a plain table (one cached request) with a split-view detail.

interface Search {
  company?: string | undefined;
}

export const Route = createFileRoute("/_app/settings/companies")({
  validateSearch: (raw: Record<string, unknown>): Search =>
    typeof raw.company === "string" && raw.company ? { company: raw.company.slice(0, 64) } : {},
  loader: ({ context }) => context.queryClient.ensureQueryData(companyListQuery),
  component: CompaniesPage,
});

function CompaniesPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const setSearch = useCallback((s: Search) => void navigate({ search: s, replace: true }), [navigate]);
  const companyList = useQuery(companyListQuery);
  const allCompanies = useMemo(() => companyList.data ?? [], [companyList.data]);
  const selected = allCompanies.find((c) => c.id === search.company);
  const columns = useMemo<TableColumn<Company>[]>(
    () => [
      { key: "code", header: t("companies.colCode"), type: "text", value: (c) => c.code },
      { key: "name", header: t("companies.colName"), type: "text", value: (c) => c.name },
      { key: "tax", header: t("companies.taxId"), type: "text", value: (c) => c.tax_id },
      { key: "city", header: t("companies.city"), type: "text", value: (c) => c.city },
      { key: "currency", header: t("companies.colCurrency"), type: "text", value: (c) => c.currency },
    ],
    [t],
  );
  const table = useTableView(allCompanies, columns);
  const companies = table.rows;
  const exportColumns: ExportColumn<Company>[] = [
    { header: t("companies.colCode"), kind: "code", value: (c) => c.code },
    { header: t("companies.colName"), kind: "text", value: (c) => c.name },
    { header: t("companies.taxId"), kind: "code", value: (c) => c.tax_id },
    { header: t("companies.city"), kind: "text", value: (c) => c.city },
    { header: t("companies.colCurrency"), kind: "code", value: (c) => c.currency },
    { header: t("export.active"), kind: "boolean", value: (c) => c.is_active },
  ];

  return (
    <div className="flex min-h-0 grow flex-col gap-4">
      <header className="flex flex-col gap-0.5">
        <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
          {t("nav.settingsGroup.title")} <span aria-hidden="true">/</span> {t("nav.settingsGroup.companies")}
        </nav>
        <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("companies.title")}</h1>
      </header>
      <div className="flex min-h-0 grow gap-4">
        <Card aria-label={t("companies.listLabel")} className="min-w-0 grow self-start overflow-hidden">
          <TableToolbar
            label={t("companies.listLabel")}
            tools={{ id: "companies", title: t("companies.title") }}
            exportColumns={exportColumns}
            rows={companies}
            rowId={(c) => c.id}
            selectedId={search.company}
            onSelect={(c) => setSearch({ company: c.id })}
            onRefresh={() => void companyList.refetch()}
            refreshing={companyList.isRefetching}
            hasView={table.hasView}
            onClearView={table.clear}
          />
          {table.chips && <div className="border-b border-border-subtle px-3 py-2">{table.chips}</div>}
          {table.popover}
          <table className="tabular w-full border-collapse text-[13px]">
            <thead>
              <tr className="bg-surface-muted text-left text-ink-muted">
                {table.th("code")}
                {table.th("name")}
                {table.th("tax")}
                {table.th("city")}
                {table.th("currency")}
              </tr>
            </thead>
            <tbody>
              {companies.map((c) => (
                <tr
                  key={c.id}
                  aria-selected={c.id === search.company}
                  onClick={() => setSearch({ company: c.id })}
                  className={cn(
                    "cursor-pointer border-t border-border-subtle hover:bg-surface-hover",
                    c.id === search.company && "bg-accent-soft",
                  )}
                >
                  <td className="px-4 py-2.5 font-mono text-xs font-semibold">
                    <button type="button" className="text-left" onClick={() => setSearch({ company: c.id })}>
                      {c.code}
                    </button>
                  </td>
                  <td className="px-4 py-2.5">
                    {c.name} {!c.is_active && <Badge tone="neutral">{t("companies.inactive")}</Badge>}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs">{c.tax_id ?? "—"}</td>
                  <td className="px-4 py-2.5">{c.city ?? "—"}</td>
                  <td className="px-4 py-2.5">{c.currency}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <DetailPanel
          label={t("companies.detailLabel")}
          open={!!selected}
          placeholder={<DetailPlaceholder title={t("companies.selectPrompt")} hint={t("companies.selectPromptHint")} />}
        >
          {selected && <CompanyPanel key={selected.id} company={selected} onClose={() => setSearch({})} />}
        </DetailPanel>
      </div>
    </div>
  );
}
